import { useState, useMemo } from "react";
import {
  useAdminCables,
  useAdminCablePlans,
  useCreateAdminCable,
  useUpdateAdminCable,
  useDeleteAdminCable,
  useCreateAdminCablePlans,
  useUpdateAdminCablePlan,
  useDeleteAdminCablePlan,
  useAdminApiProviders,
} from "../../../api/admin";
import { toast } from "react-hot-toast";

// Backend response types
interface BackendCable {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  cable_id: string;
  logo_url: string | null;
  provider: number;
}

interface BackendCablePlan {
  id: number;
  cable_name: string;
  cable: number;
  created_at: string;
  updated_at: string;
  name: string;
  amount: string;
  plan_id: string;
  is_active: boolean;
  commission: string | null;
  provider: number;
}

interface BackendProvider {
  id: number;
  name: string;
  is_active: boolean;
}

interface Cable {
  id: number;
  name: string;
  cableId: string;
  logoUrl: string | null;
  provider: number;
  providerName: string;
  createdAt: string;
}

interface CablePlan {
  id: number;
  cable: string;
  cableId: number;
  name: string;
  amount: number;
  planId: string;
  isActive: boolean;
  commission: number | null;
  provider: number;
  providerName: string;
  createdAt: string;
}

export default function CableTVManagement() {
  // API Hooks
  const {
    data: cablesData,
    isLoading: cablesLoading,
    refetch: refetchCables,
  } = useAdminCables();
  const {
    data: plansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = useAdminCablePlans();
  const { data: providersResponse } = useAdminApiProviders();

  const createCable = useCreateAdminCable();
  const updateCable = useUpdateAdminCable();
  const deleteCable = useDeleteAdminCable();

  const createPlan = useCreateAdminCablePlans();
  const updatePlan = useUpdateAdminCablePlan();
  const deletePlan = useDeleteAdminCablePlan();

  // Local State
  const [activeTab, setActiveTab] = useState<"providers" | "plans">(
    "providers",
  );
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingCable, setEditingCable] = useState<Cable | null>(null);
  const [editingPlan, setEditingPlan] = useState<CablePlan | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [cableFormData, setCableFormData] = useState({
    name: "",
    cable_id: "",
    logo_url: "",
    provider: "",
  });

  const [planFormData, setPlanFormData] = useState({
    cable: "",
    name: "",
    amount: "",
    plan_id: "",
    commission: "",
    provider: "",
    is_active: true,
  });

  // Extract providers from nested response
  const providers = useMemo(() => {
    if (!providersResponse?.results?.data) return [];
    return providersResponse.results.data as BackendProvider[];
  }, [providersResponse]);

  // Map cables
  const cables = useMemo(() => {
    if (!cablesData || !Array.isArray(cablesData)) return [];

    return (cablesData as BackendCable[]).map((cable) => {
      const provider = providers.find((p) => p.id === cable.provider);

      return {
        id: cable.id,
        name: cable.name,
        cableId: cable.cable_id,
        logoUrl: cable.logo_url,
        provider: cable.provider,
        providerName: provider?.name || `Provider ${cable.provider}`,
        createdAt: new Date(cable.created_at).toLocaleDateString(),
      };
    });
  }, [cablesData, providers]);

  // Map cable plans
  const cablePlans = useMemo(() => {
    if (!plansData || !Array.isArray(plansData)) return [];

    return (plansData as BackendCablePlan[]).map((plan) => {
      const provider = providers.find((p) => p.id === plan.provider);

      return {
        id: plan.id,
        cable: plan.cable_name,
        cableId: plan.cable,
        name: plan.name,
        amount: parseFloat(plan.amount),
        planId: plan.plan_id,
        isActive: plan.is_active,
        commission: plan.commission ? parseFloat(plan.commission) : null,
        provider: plan.provider,
        providerName: provider?.name || `Provider ${plan.provider}`,
        createdAt: new Date(plan.created_at).toLocaleDateString(),
      };
    });
  }, [plansData, providers]);

  // Filter cables
  const filteredCables = useMemo(() => {
    return cables.filter(
      (cable) =>
        cable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cable.cableId.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [cables, searchTerm]);

  // Filter plans
  const filteredPlans = useMemo(() => {
    return cablePlans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.cable.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [cablePlans, searchTerm]);

  // Cable CRUD handlers
  const handleCableSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: cableFormData.name,
        cable_id: cableFormData.cable_id,
        logo_url: cableFormData.logo_url || null,
        provider: parseInt(cableFormData.provider),
      };

      if (editingCable) {
        await updateCable.mutateAsync({
          id: editingCable.id,
          data: payload,
        });
        toast.success("Cable provider updated successfully!");
      } else {
        await createCable.mutateAsync(payload);
        toast.success("Cable provider created successfully!");
      }

      refetchCables();
      resetCableForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save cable provider";
      toast.error(errorMessage);
      console.error("Error saving cable:", error);
    }
  };

  const handleCableEdit = (cable: Cable) => {
    setEditingCable(cable);
    setCableFormData({
      name: cable.name,
      cable_id: cable.cableId,
      logo_url: cable.logoUrl || "",
      provider: cable.provider.toString(),
    });
    setShowProviderModal(true);
  };

  const handleCableDelete = async (cableId: number) => {
    if (!confirm("Are you sure you want to delete this cable provider?"))
      return;

    try {
      await deleteCable.mutateAsync(cableId);
      toast.success("Cable provider deleted successfully!");
      refetchCables();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete cable provider";
      toast.error(errorMessage);
      console.error("Error deleting cable:", error);
    }
  };

  const resetCableForm = () => {
    setCableFormData({
      name: "",
      cable_id: "",
      logo_url: "",
      provider: "",
    });
    setEditingCable(null);
    setShowProviderModal(false);
  };

  // Plan CRUD handlers
  const handlePlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        cable: parseInt(planFormData.cable),
        name: planFormData.name,
        amount: planFormData.amount,
        plan_id: planFormData.plan_id,
        commission: planFormData.commission || null,
        provider: parseInt(planFormData.provider),
        is_active: planFormData.is_active,
      };

      if (editingPlan) {
        await updatePlan.mutateAsync({
          id: editingPlan.id,
          data: payload,
        });
        toast.success("Cable plan updated successfully!");
      } else {
        await createPlan.mutateAsync(payload);
        toast.success("Cable plan created successfully!");
      }

      refetchPlans();
      resetPlanForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save cable plan";
      toast.error(errorMessage);
      console.error("Error saving plan:", error);
    }
  };

  const handlePlanEdit = (plan: CablePlan) => {
    setEditingPlan(plan);
    setPlanFormData({
      cable: plan.cableId.toString(),
      name: plan.name,
      amount: plan.amount.toString(),
      plan_id: plan.planId,
      commission: plan.commission?.toString() || "",
      provider: plan.provider.toString(),
      is_active: plan.isActive,
    });
    setShowPlanModal(true);
  };

  const handlePlanDelete = async (planId: number) => {
    if (!confirm("Are you sure you want to delete this cable plan?")) return;

    try {
      await deletePlan.mutateAsync(planId);
      toast.success("Cable plan deleted successfully!");
      refetchPlans();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete cable plan";
      toast.error(errorMessage);
      console.error("Error deleting plan:", error);
    }
  };

  const resetPlanForm = () => {
    setPlanFormData({
      cable: "",
      name: "",
      amount: "",
      plan_id: "",
      commission: "",
      provider: "",
      is_active: true,
    });
    setEditingPlan(null);
    setShowPlanModal(false);
  };

  // Loading state
  if (cablesLoading || plansLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
            <p className="mt-2 text-gray-600">Loading cable TV data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Cable TV Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage cable TV providers and subscription plans
          </p>
        </div>
        <button
          onClick={() =>
            activeTab === "providers"
              ? setShowProviderModal(true)
              : setShowPlanModal(true)
          }
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          Add {activeTab === "providers" ? "Provider" : "Plan"}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
          <button
            onClick={() => setActiveTab("providers")}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              activeTab === "providers"
                ? "bg-white text-red-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <i className="ri-tv-fill mr-2"></i>
            Cable Providers ({cables.length})
          </button>
          <button
            onClick={() => setActiveTab("plans")}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              activeTab === "plans"
                ? "bg-white text-red-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <i className="ri-list-check-fill mr-2"></i>
            Cable Plans ({cablePlans.length})
          </button>
        </div>

        {/* Search */}
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search {activeTab === "providers" ? "Providers" : "Plans"}
          </label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${activeTab === "providers" ? "providers" : "plans"}...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === "providers" ? (
        /* Providers Grid */
        <>
          {filteredCables.length === 0 && (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
              <i className="ri-tv-line text-5xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Cable Providers Found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Get started by adding your first cable provider"}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowProviderModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <i className="ri-add-fill mr-2"></i>
                  Add Provider
                </button>
              )}
            </div>
          )}

          {filteredCables.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCables.map((cable) => (
                <div
                  key={cable.id}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    {cable.logoUrl ? (
                      <img
                        src={cable.logoUrl}
                        alt={cable.name}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://via.placeholder.com/48?text=TV";
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-tv-fill text-purple-600 text-xl"></i>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {cable.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {cable.providerName}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Cable ID:</span>
                      <span className="font-medium">#{cable.cableId}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Provider ID:</span>
                      <span className="font-medium">#{cable.id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Created:</span>
                      <span className="font-medium">{cable.createdAt}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCableEdit(cable)}
                      className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm whitespace-nowrap"
                    >
                      <i className="ri-edit-fill mr-1"></i>
                      Edit
                    </button>
                    <button
                      onClick={() => handleCableDelete(cable.id)}
                      className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-fill mr-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Plans Table */
        <>
          {filteredPlans.length === 0 && (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
              <i className="ri-list-check text-5xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Cable Plans Found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Get started by adding your first cable plan"}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowPlanModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <i className="ri-add-fill mr-2"></i>
                  Add Plan
                </button>
              )}
            </div>
          )}

          {filteredPlans.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cable Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan ID (API)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlans.map((plan) => (
                      <tr key={plan.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {plan.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {plan.id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {plan.cable}
                          </div>
                          <div className="text-sm text-gray-500">
                            {plan.providerName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {plan.planId}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₦{plan.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {plan.commission ? `${plan.commission}%` : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              plan.isActive
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {plan.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handlePlanEdit(plan)}
                              className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <i className="ri-edit-fill"></i>
                            </button>
                            <button
                              onClick={() => handlePlanDelete(plan.id)}
                              className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <i className="ri-delete-bin-fill"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Provider Modal */}
      {showProviderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingCable ? "Edit Cable Provider" : "Add New Provider"}
                </h3>
                <button
                  onClick={resetCableForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleCableSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider Name
                  </label>
                  <input
                    type="text"
                    value={cableFormData.name}
                    onChange={(e) =>
                      setCableFormData({
                        ...cableFormData,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., DSTV"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cable ID{" "}
                    <span className="text-gray-500">(from API provider)</span>
                  </label>
                  <input
                    type="text"
                    value={cableFormData.cable_id}
                    onChange={(e) =>
                      setCableFormData({
                        ...cableFormData,
                        cable_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 2"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This is the unique ID from your API provider
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Provider
                  </label>
                  <select
                    value={cableFormData.provider}
                    onChange={(e) =>
                      setCableFormData({
                        ...cableFormData,
                        provider: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={cableFormData.logo_url}
                    onChange={(e) =>
                      setCableFormData({
                        ...cableFormData,
                        logo_url: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createCable.isPending || updateCable.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createCable.isPending || updateCable.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingCable ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        {editingCable ? "Update Provider" : "Create Provider"}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetCableForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPlan ? "Edit Cable Plan" : "Add New Plan"}
                </h3>
                <button
                  onClick={resetPlanForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handlePlanSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cable Provider
                  </label>
                  <select
                    value={planFormData.cable}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        cable: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Cable Provider</option>
                    {cables.map((cable) => (
                      <option key={cable.id} value={cable.id}>
                        {cable.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Name
                  </label>
                  <input
                    type="text"
                    value={planFormData.name}
                    onChange={(e) =>
                      setPlanFormData({ ...planFormData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., DStv Padi"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan ID{" "}
                    <span className="text-gray-500">(from API provider)</span>
                  </label>
                  <input
                    type="text"
                    value={planFormData.plan_id}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        plan_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 1"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This is the unique plan ID from your API provider
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Provider
                  </label>
                  <select
                    value={planFormData.provider}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        provider: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₦)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={planFormData.amount}
                      onChange={(e) =>
                        setPlanFormData({
                          ...planFormData,
                          amount: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={planFormData.commission}
                      onChange={(e) =>
                        setPlanFormData({
                          ...planFormData,
                          commission: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="plan_is_active"
                    checked={planFormData.is_active}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        is_active: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label
                    htmlFor="plan_is_active"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active Plan
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createPlan.isPending || updatePlan.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createPlan.isPending || updatePlan.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingPlan ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{editingPlan ? "Update Plan" : "Create Plan"}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetPlanForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
